/**
 * BillingProducts — Admin Products CRUD UI
 *
 * @see US2, AC-4 through AC-9 in docs/specifications/2026-03-08-billing-admin-ui.md
 */
import React, { useState, useEffect } from "react";
import Layout from "../../../components/Layout";
import { EmptyState } from "../../../components/common/EmptyState";
import { useBillingAdminBrand } from "../../../hooks/useBillingAdminBrand";
import { useProductsQuery } from "../../../hooks/useAdminProductCatalog";
import BillingBrandGuard from "./BillingBrandGuard";
import BillingBrandSelector from "./BillingBrandSelector";
import type { BillingProduct, ProductStatus } from "../../../types/adminBilling";
import { ProductFormModal } from "./components/ProductFormModal";
import { StatusTransitionModal } from "./components/StatusTransitionModal";
import { ProductDetailModal } from "./components/ProductDetailModal";
import {
  PRODUCT_STATUS_LABELS,
  PRODUCT_STATUS_CLASSES,
  PRODUCT_TYPE_LABELS,
  PRODUCT_TYPE_CLASSES,
} from "./components/constants";

const PAGE_LIMIT = 20;

// =============================================================================
// MAIN PAGE
// =============================================================================

const BillingProducts: React.FC = () => {
  const { selectedBrand, brands, isLoading, selectBrand, isBrandRequired } =
    useBillingAdminBrand();

  const [page, setPage] = useState(0);
  const offset = page * PAGE_LIMIT;

  const { data, isLoading: isLoadingProducts, isError: isErrorProducts, refetch: refetchProducts } = useProductsQuery(
    selectedBrand?.id ?? null,
    { limit: PAGE_LIMIT, offset }
  );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const [editingProduct, setEditingProduct] = useState<BillingProduct | null>(
    null
  );
  const [statusTransition, setStatusTransition] = useState<{
    product: BillingProduct;
    target: ProductStatus;
  } | null>(null);

  const products = data?.data ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? PAGE_LIMIT;

  // Reset page when brand changes
  useEffect(() => {
    setPage(0);
  }, [selectedBrand?.id]);

  return (
    <Layout>
      <BillingBrandGuard
        isBrandRequired={isBrandRequired}
        brands={brands}
        isLoading={isLoading}
        onSelectBrand={selectBrand}
      >
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                Produkty Billing
              </h1>
              <p className="text-neutral-600 mt-1 text-sm">
                Zarządzaj produktami (LEAF/PACKAGE) i ich features
              </p>
            </div>
            <div className="flex items-center gap-4">
              <BillingBrandSelector
                brands={brands}
                selectedBrand={selectedBrand}
                onSelectBrand={selectBrand}
              />
              {selectedBrand && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  + Nowy produkt
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          {isErrorProducts ? (
            <div className="p-6 text-center">
              <p className="text-red-600">Wystąpił błąd podczas ładowania danych.</p>
              <button onClick={() => refetchProducts()} className="mt-2 text-primary-700 underline">
                Spróbuj ponownie
              </button>
            </div>
          ) : isLoadingProducts ? (
            <div className="flex items-center justify-center min-h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white rounded-lg border border-neutral-200">
              <EmptyState
                title="Brak produktów"
                description="Nie znaleziono żadnych produktów dla wybranego brandu."
                action={
                  selectedBrand
                    ? {
                        label: "Nowy produkt",
                        onClick: () => setShowCreateModal(true),
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Nazwa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Typ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Features
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Utworzony
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Akcje
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {products.map((product) => (
                      <tr
                        key={product.id}
                        className="hover:bg-neutral-50 cursor-pointer"
                        onClick={() => setSelectedProductId(product.id)}
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-neutral-900">
                            {product.name}
                          </div>
                          {product.description && (
                            <div className="text-sm text-neutral-500 truncate max-w-xs">
                              {product.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              PRODUCT_TYPE_CLASSES[product.productType]
                            }`}
                          >
                            {PRODUCT_TYPE_LABELS[product.productType]}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              PRODUCT_STATUS_CLASSES[product.status as ProductStatus]
                            }`}
                          >
                            {PRODUCT_STATUS_LABELS[product.status as ProductStatus]}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                          {product.featuresCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                          {new Date(product.createdAt).toLocaleDateString(
                            "pl-PL"
                          )}
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-right text-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => setSelectedProductId(product.id)}
                            className="text-primary-600 hover:text-primary-800 font-medium"
                          >
                            Szczegóły
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {total > limit && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 text-sm text-gray-600">
                  <span>
                    Wyświetlanie {offset + 1}-{Math.min(offset + limit, total)} z {total} rekordów
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={offset === 0}
                      onClick={() => setPage((p) => p - 1)}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Poprzednia
                    </button>
                    <button
                      disabled={offset + limit >= total}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Następna
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </BillingBrandGuard>

      {/* Create Modal */}
      {showCreateModal && selectedBrand && (
        <ProductFormModal
          brandId={selectedBrand.id}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {}}
        />
      )}

      {/* Detail Modal */}
      {selectedProductId && selectedBrand && (
        <ProductDetailModal
          productId={selectedProductId}
          brandId={selectedBrand.id}
          onClose={() => {
            setSelectedProductId(null);
            setEditingProduct(null);
          }}
          onEditProduct={() => {
            const product = products.find((p) => p.id === selectedProductId);
            if (product) {
              setEditingProduct(product);
              setSelectedProductId(null);
            }
          }}
          onTransition={(target) => {
            const product = products.find((p) => p.id === selectedProductId);
            if (product) {
              setStatusTransition({ product, target });
              setSelectedProductId(null);
            }
          }}
        />
      )}

      {/* Edit Modal */}
      {editingProduct && selectedBrand && (
        <ProductFormModal
          product={editingProduct}
          brandId={selectedBrand.id}
          onClose={() => setEditingProduct(null)}
          onSuccess={() => {}}
        />
      )}

      {/* Status Transition Modal */}
      {statusTransition && selectedBrand && (
        <StatusTransitionModal
          product={statusTransition.product}
          targetStatus={statusTransition.target}
          brandId={selectedBrand.id}
          onClose={() => setStatusTransition(null)}
          onSuccess={() => setStatusTransition(null)}
        />
      )}
    </Layout>
  );
};

export default BillingProducts;
